import { useState } from "react";

import { AiOutlineClose } from "react-icons/ai";

import Button from "@/components/Button";
import ModalLayout from "@/components/Layout/ModalLayout";

export interface VehicleInput {
  vehicleName: string;
  vehicleNo: string;
}

interface Props {
  record: VehicleInput;
  isNew: boolean;
  onSave: (record: VehicleInput) => void;
  onClose: () => void;
}

const fields: { key: keyof VehicleInput; label: string; placeholder: string }[] =
  [
    { key: "vehicleName", label: "차량명", placeholder: "예: 스타렉스 1호" },
    { key: "vehicleNo", label: "차량번호", placeholder: "예: 78보 3232" },
  ];

const VehicleEditModal = ({ record, isNew, onSave, onClose }: Props) => {
  const [form, setForm] = useState<VehicleInput>({ ...record });
  const [touched, setTouched] = useState<Set<keyof VehicleInput>>(new Set());

  const handleChange = (key: keyof VehicleInput, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => new Set(prev).add(key));
  };

  const handleSubmit = () => {
    const missing = fields
      .map((f) => f.key)
      .filter((k) => !form[k].trim());
    if (missing.length > 0) {
      setTouched((prev) => {
        const next = new Set(prev);
        missing.forEach((k) => next.add(k));
        return next;
      });
      return;
    }
    onSave({
      vehicleName: form.vehicleName.trim(),
      vehicleNo: form.vehicleNo.trim(),
    });
  };

  const isFieldError = (key: keyof VehicleInput) =>
    touched.has(key) && !form[key].trim();

  return (
    <ModalLayout variant="primary" onClose={onClose}>
      <div className="relative z-50 flex w-[420px] flex-col px-6 py-5">
        {/* 헤더 */}
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">
            {isNew ? "차량 추가" : "차량 수정"}
          </h3>
          <Button variant="icon" onClick={onClose}>
            <AiOutlineClose size={18} />
          </Button>
        </div>

        <div className="flex flex-col gap-3">
          {fields.map(({ key, label, placeholder }) => {
            const error = isFieldError(key);
            return (
              <div key={key}>
                <label className="mb-1 block text-xs font-medium text-gray-500">
                  {label}
                  <span className="ml-0.5 text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={form[key]}
                  placeholder={placeholder}
                  onChange={(e) => handleChange(key, e.target.value)}
                  className={`h-10 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1 ${
                    error
                      ? "border-red-300 focus:border-red-400 focus:ring-red-200"
                      : "border-gray-200 focus:border-primary focus:ring-primary/20"
                  }`}
                />
                {error && (
                  <p className="mt-0.5 text-xs text-red-400">
                    {label}을(를) 입력해주세요.
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* 하단 버튼 */}
        <div className="mt-5 flex justify-end gap-2">
          <Button
            variant="custom"
            onClick={onClose}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
          >
            취소
          </Button>
          <Button
            variant="custom"
            onClick={handleSubmit}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            {isNew ? "추가" : "저장"}
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
};

export default VehicleEditModal;
